/**
 * Shared HTTP helper for the API layer.
 *
 * Replaces the repeated `fetch → text() → JSON.parse → catch(new Error)`
 * chain. Differences from that chain, on purpose:
 * - a network failure or a non-JSON body **throws** (callers already wrap the
 *   calls in try/catch and show `getErrorMessage(error)`), instead of
 *   resolving to an `Error` object that crashed on `.detail`;
 * - accepts an `AbortSignal` so react-query and route changes can cancel
 *   requests that nobody is waiting for any more.
 */
import {
  getLocalStorageToken,
  removeLocalStorageToken,
} from '../helpers/localStorage';

/** Message `useErrorMessage` already treats as "drop the session and go to /login". */
export const INVALID_SESSION_MESSAGE = 'Sessão inválida';

/**
 * What the API answers for an expired or unreadable JWT. The API also uses
 * 401 for errors that are not session related (wrong password, missing
 * permission), so the session ends on these messages, not on the status.
 */
export const UNAUTHENTICATED_MESSAGE = 'Usuário não autenticado';

export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

export async function requestJson<T>(
  url: string,
  init?: RequestInit,
  signal?: AbortSignal
): Promise<T> {
  const options: RequestInit = signal ? { ...init, signal } : init ?? {};
  const response = await fetch(url, options);
  // A 401 with a stored session token means the token expired or was revoked:
  // end the session the same way every screen already handles it. The MFA
  // pending token lives under another key and never reaches this branch.
  if (response.status === 401 && getLocalStorageToken()) {
    removeLocalStorageToken();
    throw new HttpError(401, INVALID_SESSION_MESSAGE);
  }
  const text = await response.text();
  if (text.length === 0) {
    if (response.ok) return {} as T;
    throw new HttpError(response.status, response.statusText);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new HttpError(
      response.status,
      response.ok ? 'Resposta inválida do servidor.' : response.statusText
    );
  }
}
